import React, { useState } from 'react';
import LoadingSpinner from '../common/LoadingSpinner';

interface DocumentActionModalProps {
  document: {
    id: number;
    original_filename: string;
    document_type: string;
    status: string;
  };
  action: 'approve' | 'reject';
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const DocumentActionModal: React.FC<DocumentActionModalProps> = ({
  document,
  action,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // For rejection, comment is required
    if (action === 'reject' && !comment.trim()) {
      setError('Please provide a reason for rejection');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch('/backend/api/admin/documents/update-status.php', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          document_id: document.id,
          status: action === 'approve' ? 'approved' : 'rejected',
          admin_comment: comment.trim()
        }),
      });

      const result = await response.json();

      if (result.success) {
        onSuccess();
        onClose();
        // Reset form
        setComment('');
      } else {
        setError(result.error || `Failed to ${action} document`);
      }
    } catch (err) {
      setError(`An error occurred while ${action === 'approve' ? 'approving' : 'rejecting'} the document`);    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const isApprove = action === 'approve';
  const actionText = isApprove ? 'Approve' : 'Reject';
  const actionColor = isApprove ? 'green' : 'red';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-md w-full">
        <div className="px-6 py-4 border-b flex justify-between items-center">
          <h2 className="text-xl font-semibold">{actionText} Document</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            disabled={loading}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6">
          {/* Document Information */}
          <div className="mb-4 p-4 bg-gray-50 rounded-lg">
            <h3 className="font-medium text-gray-900 mb-2">Document Details</h3>
            <div className="text-sm text-gray-600 space-y-1">
              <div><strong>File:</strong> {document.original_filename}</div>
              <div><strong>Type:</strong> {document.document_type}</div>
              <div><strong>Current Status:</strong> 
                <span className={`ml-1 px-2 py-1 rounded text-xs font-medium ${
                  document.status === 'approved' ? 'bg-green-100 text-green-800' :
                  document.status === 'rejected' ? 'bg-red-100 text-red-800' :
                  'bg-yellow-100 text-yellow-800'
                }`}>
                  {document.status}
                </span>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Comment Field */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {isApprove ? 'Comment (Optional)' : 'Reason for Rejection *'}
              </label>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                rows={4}
                placeholder={isApprove ? 
                  'Add any additional notes about this approval...' : 
                  'Please provide a clear reason for rejecting this document...'
                }
                disabled={loading}
                required={!isApprove}
              />
              {!isApprove && (
                <p className="text-xs text-gray-500 mt-1">
                  This reason will be sent to the client. The request status will be changed to "Documents Requested" to allow the client to reupload.
                </p>
              )}
            </div>

            {/* Warning for Rejection */}
            {!isApprove && (
              <div className="p-3 bg-blue-100 border border-blue-300 rounded-md">
                <div className="flex">
                  <svg className="w-5 h-5 text-blue-400 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div className="text-sm text-blue-700">
                    <p className="font-medium">Request Status Will Change</p>
                    <p>The client will be notified about the rejection. The request status will automatically change to "Documents Requested" and the client will be able to upload a corrected document from their request details page.</p>
                  </div>
                </div>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="p-3 bg-red-100 border border-red-300 rounded-md">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className={`px-4 py-2 text-white rounded-md hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center ${
                  isApprove ? 'bg-green-500 hover:bg-green-600' : 'bg-red-500 hover:bg-red-600'
                }`}
                disabled={loading || (!isApprove && !comment.trim())}
              >
                {loading ? (
                  <>
                    <LoadingSpinner />
                    <span className="ml-2">{isApprove ? 'Approving...' : 'Rejecting...'}</span>
                  </>
                ) : (
                  `${actionText} Document`
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default DocumentActionModal;